import { useCallback, useRef, useState } from 'react';
import { roaryApi } from '../api/modules/roary';
import type { RoaryBuildProgress, RoaryBuildPageProgress } from './useRoary';
import type { AddPageWithRoaryValues } from '../components/Roary/AddPageWithRoaryModal';

export interface RoaryBuildPageComplete {
  siteId: string;
  siteName: string;
  pageId: string;
  pageTitle?: string;
  pageSlug?: string;
}

interface ConnectedData {
  sessionId?: string;
}

interface PlanReadyData {
  plan?: {
    businessName?: string;
    businessType?: string;
    templateName?: string;
    pages?: Array<{ slug: string; title: string; intent?: string }>;
  };
}

interface PageEventData {
  pageId?: string;
  title?: string;
  slug?: string;
  issues?: string[];
  componentsUpdated?: number;
  attempt?: number;
  verified?: boolean;
}

interface CompleteData {
  pageId?: string;
  siteId?: string;
  siteName?: string;
  pageTitle?: string;
  pageSlug?: string;
}

function patchPage(
  pages: RoaryBuildPageProgress[],
  update: Partial<RoaryBuildPageProgress> & { slug?: string; title?: string }
): RoaryBuildPageProgress[] {
  if (pages.length === 0) {
    return [
      {
        slug: update.slug || '',
        title: update.title || 'New page',
        status: update.status || 'pending',
        componentsUpdated: update.componentsUpdated || 0,
        attempts: update.attempts || 1,
        issues: update.issues || [],
      },
    ];
  }
  return pages.map((page, i) => (i === 0 ? { ...page, ...update } : page));
}

export function useRoaryBuildPage() {
  const [isBuilding, setIsBuilding] = useState(false);
  const [buildProgress, setBuildProgress] = useState<RoaryBuildProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<(() => void) | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const completeRef = useRef<RoaryBuildPageComplete | null>(null);
  const errorRef = useRef<string | null>(null);

  const startBuild = useCallback(
    (
      siteId: string,
      values: AddPageWithRoaryValues,
      onComplete: (result: RoaryBuildPageComplete) => void
    ) => {
      abortRef.current?.();
      completeRef.current = null;
      sessionIdRef.current = null;
      errorRef.current = null;
      setError(null);
      setIsBuilding(true);
      setBuildProgress({
        plan: null,
        phase: 'planning',
        pages: [],
        layouts: [],
        currentPageIndex: null,
        currentLayoutIndex: null,
      });

      abortRef.current = roaryApi.buildPage(
        {
          siteId,
          pageType: values.pageType,
          customTitle: values.customTitle,
          purpose: values.purpose,
          addToNav: values.addToNav,
        },
        (event, raw) => {
          const data = (raw || {}) as Record<string, unknown>;

          if (event === 'connected') {
            sessionIdRef.current = (data as ConnectedData).sessionId || null;
            return;
          }

          if (event === 'build_plan_ready') {
            const plan = (data as PlanReadyData).plan;
            setBuildProgress(prev => ({
              plan: plan
                ? {
                    businessName: plan.businessName || '',
                    businessType: plan.businessType || '',
                    templateName: plan.templateName || '',
                  }
                : prev?.plan || null,
              phase: 'scaffolding',
              pages: (plan?.pages || []).map(p => ({
                slug: p.slug,
                title: p.title,
                status: 'pending' as const,
                componentsUpdated: 0,
                attempts: 0,
                issues: [],
              })),
              layouts: [],
              currentPageIndex: 0,
              currentLayoutIndex: null,
            }));
            return;
          }

          setBuildProgress(prev => {
            if (!prev) return prev;
            if (event === 'build_scaffold_started') {
              return { ...prev, phase: 'scaffolding' };
            }
            if (event === 'build_scaffold_completed') {
              return { ...prev, phase: 'building_pages' };
            }
            if (event === 'build_page_started') {
              const page = data as PageEventData;
              return {
                ...prev,
                phase: 'building_pages',
                currentPageIndex: 0,
                pages: patchPage(prev.pages, {
                  slug: page.slug,
                  title: page.title,
                  status: 'customizing',
                }),
              };
            }
            if (event === 'build_page_customized') {
              const page = data as PageEventData;
              return {
                ...prev,
                pages: patchPage(prev.pages, {
                  status: 'verifying',
                  componentsUpdated: page.componentsUpdated,
                  attempts: page.attempt,
                }),
              };
            }
            if (event === 'build_page_verifying') {
              return {
                ...prev,
                pages: patchPage(prev.pages, { status: 'verifying' }),
              };
            }
            if (event === 'build_page_verified') {
              const page = data as PageEventData;
              return {
                ...prev,
                pages: patchPage(prev.pages, {
                  status: page.verified ? 'verified' : 'retrying',
                  issues: page.issues || [],
                  attempts: page.attempt,
                }),
              };
            }
            if (event === 'build_page_retrying') {
              const page = data as PageEventData;
              return {
                ...prev,
                pages: patchPage(prev.pages, {
                  status: 'retrying',
                  issues: page.issues || [],
                  attempts: page.attempt,
                }),
              };
            }
            if (event === 'build_page_failed') {
              const page = data as PageEventData;
              return {
                ...prev,
                pages: patchPage(prev.pages, {
                  status: 'failed',
                  issues: page.issues || [],
                }),
              };
            }
            if (event === 'build_completed') {
              return { ...prev, phase: 'completed' };
            }
            return prev;
          });

          if (event === 'complete') {
            const complete = data as CompleteData;
            if (complete.pageId && complete.siteId) {
              completeRef.current = {
                siteId: complete.siteId,
                siteName: complete.siteName || '',
                pageId: complete.pageId,
                pageTitle: complete.pageTitle,
                pageSlug: complete.pageSlug,
              };
            }
          }

          if (event === 'cancelled') {
            errorRef.current = 'cancelled';
            return;
          }

          if (event === 'error') {
            const message = (data.message as string) || 'Roary could not finish this page.';
            errorRef.current = message;
            setError(message);
          }
        },
        err => {
          const message = err.message || 'Roary could not finish this page.';
          errorRef.current = message;
          setIsBuilding(false);
          setError(message);
        },
        () => {
          setIsBuilding(false);
          if (completeRef.current) {
            onComplete(completeRef.current);
          } else if (!errorRef.current) {
            const message = 'Roary could not finish this page.';
            errorRef.current = message;
            setError(message);
          }
        }
      );
    },
    []
  );

  const cancelBuild = useCallback(async () => {
    abortRef.current?.();
    abortRef.current = null;
    if (sessionIdRef.current) {
      try {
        await roaryApi.cancelSession(sessionIdRef.current);
      } catch {
        // ignore
      }
    }
    setIsBuilding(false);
  }, []);

  return { isBuilding, buildProgress, error, startBuild, cancelBuild, setError };
}
